import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { api, type AirbnbListing, type ManualReviewInput } from "@/lib/api-client";
import { useWizardStore } from "@/state/wizard-store";

/** A listing we've imported this session — enough to drive the reviews +
 *  calendar steps (which need a property_id) without a round-trip list call. */
interface ImportedProperty {
  property_id: string;
  slug?: string;
  title?: string;
  photos: number;
  amenities: number;
}

const cardBorder = { borderColor: "var(--color-border)" } as const;
const inputStyle = {
  borderColor: "var(--color-border)",
  background: "var(--color-card)",
  color: "var(--color-fg)",
} as const;

export function ImportAirbnb() {
  const { data: subData } = useQuery({ queryKey: ["subscription"], queryFn: api.subscription });
  const sub = subData?.data;
  const siteReady = !!sub?.tenant_site_id;

  const savedUrls = useWizardStore.getState().questionnaire?.airbnbListingUrls ?? [];
  const [imported, setImported] = useState<ImportedProperty[]>([]);

  // ── Site not provisioned yet ──
  if (sub && !siteReady) {
    return (
      <Shell title="Import from Airbnb" subtitle="Your site is still being built.">
        <p className="text-sm" style={{ color: "var(--color-muted)" }}>
          Come back once your site has finished provisioning, then you can import your Airbnb listings here.
        </p>
      </Shell>
    );
  }

  return (
    <Shell
      title="Import from Airbnb"
      subtitle="Pull your listing details, photos, amenities, and reviews from Airbnb into your new site."
    >
      <div className="space-y-8">
        <ListingImport
          savedUrls={savedUrls}
          imported={imported}
          onImported={(p) => setImported((prev) => [...prev, p])}
        />
        <ReviewImport imported={imported} />
        <CalendarConnect imported={imported} />
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — import listings
// ---------------------------------------------------------------------------

function ListingImport({
  savedUrls,
  imported,
  onImported,
}: {
  savedUrls: string[];
  imported: ImportedProperty[];
  onImported: (p: ImportedProperty) => void;
}) {
  const [url, setUrl] = useState(savedUrls[0] ?? "");
  const [html, setHtml] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [preview, setPreview] = useState<AirbnbListing | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Paste wins over URL — same precedence as the platform importer.
  const source = () => (html.trim() ? { html } : { url: url.trim() });
  const canAct = !!(html.trim() || url.trim());

  async function runPreview() {
    if (!canAct) return;
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const res = await api.previewAirbnbListing(source());
      if (res.blocked) {
        setShowPaste(true);
        setError(
          res.error ||
            "Airbnb blocked the automatic fetch. Paste your listing's page source below instead.",
        );
      } else if (!res.listing) {
        setError(res.error || "Couldn't read that listing — try the paste option.");
      } else {
        setPreview(res.listing);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preview failed");
    } finally {
      setBusy(false);
    }
  }

  async function runImport() {
    if (!canAct) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.importAirbnbListing(source());
      if (!res.ok || !res.property_id) {
        if (res.blocked) setShowPaste(true);
        setError(res.error || "Import failed.");
        return;
      }
      onImported({
        property_id: res.property_id,
        slug: res.slug,
        title: res.listing?.title,
        photos: res.photos_imported ?? 0,
        amenities: res.amenities_imported ?? 0,
      });
      // Reset for the next listing, pre-filling the next saved URL if any.
      const next = savedUrls.find((u) => u.trim() && u.trim() !== url.trim());
      setUrl(next ?? "");
      setHtml("");
      setPreview(null);
      setShowPaste(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2 className="text-lg font-semibold">1. Import your listings</h2>
      <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
        Each listing becomes a draft you can review and publish. Exact address and nightly price stay
        yours to set — a public Airbnb page hides those.
      </p>

      {imported.length > 0 && (
        <ul className="mt-3 space-y-2">
          {imported.map((p) => (
            <li
              key={p.property_id}
              className="rounded-md border p-3 text-sm"
              style={{ borderColor: "var(--color-primary)" }}
            >
              <span style={{ color: "var(--color-primary)" }}>✓</span>{" "}
              <strong>{p.title || p.slug || "Listing"}</strong> imported — {p.photos} photo
              {p.photos === 1 ? "" : "s"}, {p.amenities} amenit{p.amenities === 1 ? "y" : "ies"}.
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-sm font-medium">Airbnb listing URL</span>
          <div className="mt-1 flex gap-2">
            <input
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setPreview(null);
              }}
              placeholder="https://www.airbnb.com/rooms/12345678"
              className="flex-1 rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            />
            <Button variant="secondary" onClick={runPreview} disabled={busy || !canAct}>
              {busy ? "Checking…" : "Preview"}
            </Button>
          </div>
        </label>

        {savedUrls.filter(Boolean).length > 1 && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span style={{ color: "var(--color-muted)" }}>From your questionnaire:</span>
            {savedUrls.filter(Boolean).map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => {
                  setUrl(u);
                  setPreview(null);
                }}
                className="underline"
                style={{ color: "var(--color-primary)" }}
              >
                {u.replace(/^https?:\/\/(www\.)?/, "").slice(0, 40)}
              </button>
            ))}
          </div>
        )}

        {showPaste && (
          <label className="block">
            <span className="text-sm font-medium">…or paste your listing's page source</span>
            <p className="text-xs" style={{ color: "var(--color-muted)" }}>
              Open the listing in your browser → right-click → <em>View Page Source</em> → select all
              → copy → paste here. This always works, even when the automatic fetch is blocked.
            </p>
            <textarea
              value={html}
              onChange={(e) => {
                setHtml(e.target.value);
                setPreview(null);
              }}
              rows={4}
              placeholder="<!DOCTYPE html> …"
              className="mt-1 w-full rounded-md border px-3 py-2 font-mono text-xs"
              style={inputStyle}
            />
          </label>
        )}

        {preview && (
          <div className="rounded-md border p-3" style={cardBorder}>
            <div className="flex gap-3">
              {preview.photoUrls?.[0] && (
                <img
                  src={preview.photoUrls[0]}
                  alt=""
                  className="h-16 w-16 rounded object-cover"
                  style={{ borderColor: "var(--color-border)" }}
                />
              )}
              <div className="text-sm">
                <p className="font-semibold">{preview.title || "Untitled listing"}</p>
                <p style={{ color: "var(--color-muted)" }}>
                  {[
                    preview.maxGuests && `${preview.maxGuests} guests`,
                    preview.bedrooms != null && `${preview.bedrooms} bd`,
                    preview.bathrooms != null && `${preview.bathrooms} ba`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
                  {preview.photoUrls?.length ?? 0} photos · {preview.amenities?.length ?? 0} amenities
                  {preview.city ? ` · ${preview.city}${preview.region ? `, ${preview.region}` : ""}` : ""}
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <p className="text-sm" style={{ color: "#dc2626" }}>
            {error}
          </p>
        )}

        <Button onClick={runImport} disabled={busy || !canAct}>
          {busy ? "Importing…" : "Import this listing"}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — import reviews
// ---------------------------------------------------------------------------

function ReviewImport({ imported }: { imported: ImportedProperty[] }) {
  const [propertyId, setPropertyId] = useState("");
  const [token, setToken] = useState<{ token: string; api_base_url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const selected = propertyId || imported[0]?.property_id || "";

  async function generate() {
    setBusy(true);
    setError("");
    try {
      const res = await api.generateReviewImportToken();
      setToken({ token: res.token, api_base_url: res.api_base_url });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create token");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2 className="text-lg font-semibold">2. Import your reviews</h2>
      <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
        Airbnb hides guest reviews from automated fetches, so reviews come in through the free Rumio
        Review Import browser extension — it reads them from your own logged-in Airbnb tab.
      </p>

      {imported.length === 0 ? (
        <p className="mt-3 rounded-md border p-3 text-sm" style={cardBorder}>
          Import at least one listing above first — reviews attach to a listing.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Which listing?</span>
            <select
              value={selected}
              onChange={(e) => setPropertyId(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            >
              {imported.map((p) => (
                <option key={p.property_id} value={p.property_id}>
                  {p.title || p.slug || p.property_id}
                </option>
              ))}
            </select>
          </label>

          <Button variant="secondary" onClick={generate} disabled={busy}>
            {busy ? "Generating…" : token ? "Regenerate token" : "Get my import token"}
          </Button>

          {error && (
            <p className="text-sm" style={{ color: "#dc2626" }}>
              {error}
            </p>
          )}

          {token && (
            <div className="rounded-md border p-4" style={{ borderColor: "var(--color-primary)" }}>
              <p className="text-sm font-semibold">Paste these into the Rumio Review Import extension</p>
              <dl className="mt-2 space-y-2 text-sm">
                <CopyRow label="Your Rumio API URL" value={token.api_base_url} />
                <CopyRow label="Import Token" value={token.token} />
                <CopyRow label="Property ID" value={selected} />
              </dl>
              <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs" style={{ color: "var(--color-muted)" }}>
                <li>Install the Rumio Review Import browser extension (from your Rumio dashboard).</li>
                <li>Open the extension and paste the three values above.</li>
                <li>Open your Airbnb listing's reviews page while logged in.</li>
                <li>
                  Click <strong>Scrape Reviews</strong>, then <strong>Push to Site</strong>. Your
                  reviews appear on your site right away.
                </li>
              </ol>
              <p className="mt-2 text-xs" style={{ color: "var(--color-muted)" }}>
                Keep this token private — anyone with it can post reviews to your site. You can
                regenerate it anytime.
              </p>
            </div>
          )}

          <ManualReviews propertyId={selected} />
        </div>
      )}
    </section>
  );
}

/** A labeled value with a copy button. */
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <dt className="text-xs font-medium" style={{ color: "var(--color-muted)" }}>
        {label}
      </dt>
      <dd className="mt-0.5 flex items-center gap-2">
        <code className="flex-1 overflow-x-auto rounded px-2 py-1 text-xs" style={inputStyle}>
          {value}
        </code>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(value).then(
              () => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              },
              () => {},
            );
          }}
          className="shrink-0 text-xs underline"
          style={{ color: "var(--color-primary)" }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </dd>
    </div>
  );
}

/** Optional manual review entry — for hosts who'd rather type a few in. */
function ManualReviews({ propertyId }: { propertyId: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ManualReviewInput[]>([]);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [date, setDate] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  function add() {
    if (!name.trim()) return;
    setRows((prev) => [
      ...prev,
      { guest_name: name.trim(), rating, review_date: date || null, body: body.trim() || null },
    ]);
    setName("");
    setRating(5);
    setDate("");
    setBody("");
  }

  async function submit() {
    if (rows.length === 0) return;
    setBusy(true);
    setError("");
    setResult("");
    try {
      const res = await api.importReviews(propertyId, rows);
      setResult(`Imported ${res.imported}${res.skipped ? `, ${res.skipped} skipped` : ""}.`);
      setRows([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm underline"
        style={{ color: "var(--color-primary)" }}
      >
        Or add a few reviews manually
      </button>
    );
  }

  return (
    <div className="rounded-md border p-4" style={cardBorder}>
      <p className="text-sm font-semibold">Add reviews manually</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Guest name"
          className="rounded-md border px-3 py-2 text-sm"
          style={inputStyle}
        />
        <select
          value={rating}
          onChange={(e) => setRating(Number(e.target.value))}
          className="rounded-md border px-3 py-2 text-sm"
          style={inputStyle}
        >
          {[5, 4, 3, 2, 1].map((r) => (
            <option key={r} value={r}>
              {r} star{r === 1 ? "" : "s"}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-md border px-3 py-2 text-sm"
          style={inputStyle}
        />
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        placeholder="What the guest said (optional)"
        className="mt-2 w-full rounded-md border px-3 py-2 text-sm"
        style={inputStyle}
      />
      <div className="mt-2">
        <Button variant="secondary" onClick={add} disabled={!name.trim()}>
          Add to list
        </Button>
      </div>

      {rows.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs" style={{ color: "var(--color-muted)" }}>
          {rows.map((r, i) => (
            <li key={i}>
              • {r.guest_name} — {r.rating}★{r.body ? ` — ${r.body.slice(0, 60)}` : ""}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="mt-2 text-sm" style={{ color: "#dc2626" }}>
          {error}
        </p>
      )}
      {result && (
        <p className="mt-2 text-sm" style={{ color: "var(--color-primary)" }}>
          {result}
        </p>
      )}

      <div className="mt-3">
        <Button onClick={submit} disabled={busy || rows.length === 0}>
          {busy ? "Importing…" : `Import ${rows.length} review${rows.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — connect calendar
// ---------------------------------------------------------------------------

function CalendarConnect({ imported }: { imported: ImportedProperty[] }) {
  const [propertyId, setPropertyId] = useState("");
  const [icalUrl, setIcalUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const selected = propertyId || imported[0]?.property_id || "";

  async function connect() {
    if (!selected || !icalUrl.trim()) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api.connectAirbnbCalendar(selected, icalUrl.trim());
      setMsg(
        res.warning ||
          `Calendar connected${
            res.synced ? ` — ${res.synced.imported} dates blocked` : ""
          }. Airbnb-booked dates now block on your site.`,
      );
      setIcalUrl("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't connect that calendar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2 className="text-lg font-semibold">3. Sync your Airbnb calendar (optional)</h2>
      <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
        Paste your Airbnb listing's calendar export link so Airbnb-booked dates block on your Rumio
        site and you never double-book. In Airbnb: Calendar → Availability → Connect calendar → Export.
      </p>

      {imported.length === 0 ? (
        <p className="mt-3 rounded-md border p-3 text-sm" style={cardBorder}>
          Import a listing above first.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Which listing?</span>
            <select
              value={selected}
              onChange={(e) => setPropertyId(e.target.value)}
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            >
              {imported.map((p) => (
                <option key={p.property_id} value={p.property_id}>
                  {p.title || p.slug || p.property_id}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Airbnb calendar export URL</span>
            <input
              value={icalUrl}
              onChange={(e) => setIcalUrl(e.target.value)}
              placeholder="https://www.airbnb.com/calendar/ical/12345678.ics?s=…"
              className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle}
            />
          </label>

          {error && (
            <p className="text-sm" style={{ color: "#dc2626" }}>
              {error}
            </p>
          )}
          {msg && (
            <p className="text-sm" style={{ color: "var(--color-primary)" }}>
              {msg}
            </p>
          )}

          <Button onClick={connect} disabled={busy || !icalUrl.trim()}>
            {busy ? "Connecting…" : "Connect calendar"}
          </Button>
        </div>
      )}
    </section>
  );
}
